import React from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  TNaerasDashboardLevels,
  TNaerasDashboardRatio,
  TNaerasPanel,
  TNaerasRatioColumn,
  TNaerasStationLevels,
} from '@/data/types';
import PanelCard from './PanelCard';
import { COLORS, MISSING, hourLabel, isNum, metres, num, ratioLabel, signed } from '../utils/format';

interface LevelsPanelProps {
  panel: TNaerasPanel<TNaerasDashboardLevels> | undefined;
  /** §2.3 production ratio, drawn under the Προεσπέρα card. */
  ratio?: TNaerasPanel<TNaerasDashboardRatio>;
  splitHour: number | null;
}

/** Levels are neither hybrid nor hydro energy, so they stay off green and blue. */
const LINE = '#334155';
const PROJECTION = '#94a3b8';
const SPLIT_MARKER = COLORS.other;

const HOUR_TICKS = [0, 3, 6, 9, 12, 15, 18, 21, 24];

interface Point {
  x: number;
  measured?: number;
  projection?: number;
}

/**
 * One point per hour on a 0–24 axis: `measured[h]` at x = h, and
 * `projection[k]` at x = split_hour + k, so the dashed line starts on the split
 * and its last point is midnight.
 */
function toPoints(station: TNaerasStationLevels, splitHour: number | null): Point[] {
  const points: Point[] = Array.from({ length: 25 }, (_, x) => ({ x }));

  station.measured.forEach((value, hour) => {
    if (isNum(value)) points[hour].measured = value;
  });

  if (splitHour !== null) {
    station.projection?.forEach((value, k) => {
      const x = splitHour + k;
      if (isNum(value) && x <= 24) points[x].projection = value;
    });
  }

  return points;
}

const LevelTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="rounded-2xl border border-slate-100 bg-white/95 p-3 shadow-xl backdrop-blur-md">
      <p className="mb-1 text-xs font-bold text-slate-800">{hourLabel(Number(label))}</p>
      {payload
        .filter((entry: any) => isNum(entry.value))
        .map((entry: any) => (
          <div key={entry.dataKey} className="flex items-center gap-2 text-xs">
            <span className="font-medium text-slate-600">
              {entry.dataKey === 'projection' ? 'Εκτίμηση:' : 'Μέτρηση:'}
            </span>
            <span className="font-extrabold text-slate-900">{metres(entry.value)}</span>
          </div>
        ))}
    </div>
  );
};

interface StationProps {
  name: string;
  station: TNaerasStationLevels;
  splitHour: number | null;
  /** Κάτω Προεσπέρα has no estimate — its third value is the change instead. */
  hasEstimate: boolean;
  /** Extra rows under the chart, e.g. the production ratio. */
  footer?: React.ReactNode;
}

const StationCard: React.FC<StationProps> = ({ name, station, splitHour, hasEstimate, footer }) => {
  const points = toPoints(station, hasEstimate ? splitHour : null);
  const hasMeasurements = station.measured.some(isNum);
  const splitLabel = splitHour !== null ? hourLabel(splitHour) : MISSING;

  const values = [
    { label: '00:00', value: metres(station.at_00) },
    { label: splitLabel, value: metres(station.at_split) },
    hasEstimate
      ? { label: '00:00 εκτ.', value: metres(station.est_00) }
      : {
          label: 'Μεταβολή',
          value: isNum(station.delta) ? `${signed(station.delta)} m` : MISSING,
        },
  ];

  return (
    <div className="flex flex-col rounded-3xl border border-slate-200/80 bg-white p-5 shadow-sm">
      <h4 className="mb-4 text-lg font-extrabold tracking-tight text-slate-900">{name}</h4>

      <div className="mb-4 grid grid-cols-3 gap-2 rounded-2xl border border-slate-100 bg-slate-50 p-3 text-center">
        {values.map((item, index) => (
          <div key={item.label} className={index === 1 ? 'border-x border-slate-200/80' : ''}>
            <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">
              {item.label}
            </span>
            <span className="text-sm font-extrabold text-slate-800">{item.value}</span>
          </div>
        ))}
      </div>

      {/* A silent sensor is a state of the data, not an error */}
      {!hasMeasurements ? (
        <div className="flex h-44 items-center justify-center rounded-2xl bg-slate-50 text-2xl font-bold text-slate-300">
          {MISSING}
        </div>
      ) : (
        <div className="h-44 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis
                dataKey="x"
                type="number"
                domain={[0, 24]}
                ticks={HOUR_TICKS}
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 600 }}
                tickFormatter={(h) => `${h}:00`}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 600 }}
                domain={['auto', 'auto']}
                tickFormatter={(v) => num(v, 2)}
                width={44}
              />
              <Tooltip content={<LevelTooltip />} />
              {splitHour !== null && (
                <ReferenceLine
                  x={splitHour}
                  stroke="#94a3b8"
                  strokeDasharray="4 3"
                  label={{
                    value: splitLabel,
                    position: 'insideTopRight',
                    fill: '#64748b',
                    fontSize: 10,
                    fontWeight: 700,
                  }}
                />
              )}
              <Line
                dataKey="measured"
                stroke={LINE}
                strokeWidth={2.5}
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
              />
              {hasEstimate && (
                <Line
                  dataKey="projection"
                  stroke={PROJECTION}
                  strokeWidth={2}
                  strokeDasharray="5 4"
                  dot={false}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              )}
              {splitHour !== null && isNum(station.at_split) && (
                <ReferenceDot
                  x={splitHour}
                  y={station.at_split}
                  r={4}
                  fill={SPLIT_MARKER}
                  stroke="#ffffff"
                  strokeWidth={2}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-4 border-t border-slate-100 pt-3 text-[11px] font-semibold text-slate-500">
        <span className="flex items-center gap-1.5">
          <span className="h-0.5 w-4" style={{ backgroundColor: LINE }} />
          Μέτρηση
        </span>
        {hasEstimate && (
          <span className="flex items-center gap-1.5">
            <span className="w-4 border-t-2 border-dashed" style={{ borderColor: PROJECTION }} />
            Εκτίμηση
          </span>
        )}
      </div>

      {footer}
    </div>
  );
};

const TOMORROW_SOURCE: Record<'draft' | 'final' | 'job', string> = {
  draft: 'πρόχειρη προσφορά',
  final: 'οριστική προσφορά',
  job: 'προσφορά Job B',
};

/**
 * One ratio column. The arrow is the level trend the server measured — never a
 * comparison of the ratio with 1/3, which ignores pumping and so says nothing
 * about the level while the hybrid runs.
 */
const RatioCell: React.FC<{ label: string; column: TNaerasRatioColumn | null; title?: string }> = ({
  label,
  column,
  title,
}) => {
  const levels =
    column && (isNum(column.level_from_m) || isNum(column.level_to_m))
      ? `Στάθμη ${metres(column.level_from_m)} → ${metres(column.level_to_m)}`
      : undefined;

  return (
    <div title={[title, levels].filter(Boolean).join(' · ') || undefined}>
      <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
      <span className="text-sm font-extrabold text-slate-800">
        {ratioLabel(column?.ratio)}
        {column?.trend === 'up' && <span className="ml-1 text-emerald-600">↑</span>}
        {column?.trend === 'down' && <span className="ml-1 text-rose-600">↓</span>}
      </span>
    </div>
  );
};

/** §2.3 — Προεσπέρα / Κάτω Προεσπέρα production ratio, under the Προεσπέρα card. */
const RatioRow: React.FC<{ panel: TNaerasPanel<TNaerasDashboardRatio> }> = ({ panel }) => {
  if (!panel.available) {
    return (
      <div className="mt-3 rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-3 text-center text-[11px] font-medium text-slate-500">
        Λόγος παραγωγής: {panel.reason}
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-2xl border border-slate-100 bg-slate-50 p-3">
      <div className="mb-2 flex items-center justify-between text-[11px] font-semibold text-slate-500">
        <span>Λόγος παραγωγής Προεσπέρα / Κάτω</span>
        <span>αναφορά {ratioLabel(panel.neutral_ratio)}</span>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <RatioCell label="Χθες" column={panel.yesterday} />
        <div className="border-x border-slate-200/80">
          <RatioCell label="Σήμερα · εκτ." column={panel.today} />
        </div>
        <RatioCell
          label="Αύριο · προσφορά"
          column={panel.tomorrow}
          title={panel.tomorrow ? `Από ${TOMORROW_SOURCE[panel.tomorrow.source] ?? panel.tomorrow.source}` : undefined}
        />
      </div>
    </div>
  );
};

/**
 * §2.3 — Πέζι, Προεσπέρα and Κάτω Προεσπέρα, in metres only. The split
 * between measured and estimated moves forward every hour, so it is always
 * labelled with the real `split_hour`.
 */
export const LevelsPanel: React.FC<LevelsPanelProps> = ({ panel, ratio, splitHour }) => (
  <PanelCard
    title="Στάθμες"
    subtitle={
      splitHour !== null
        ? `Μέτρηση 00:00–${hourLabel(splitHour)} και εκτίμηση ${hourLabel(splitHour)}–24:00, σε μέτρα`
        : 'Ωριαία μέτρηση και εκτίμηση πορείας, σε μέτρα'
    }
    panel={panel}
    delay={0.2}
  >
    {(levels) => (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <StationCard name="Πέζι" station={levels.pezi} splitHour={splitHour} hasEstimate />
        <StationCard
          name="Προεσπέρα"
          station={levels.proespera}
          splitHour={splitHour}
          hasEstimate
          footer={ratio && <RatioRow panel={ratio} />}
        />
        <StationCard
          name="Κάτω Προεσπέρα"
          station={levels.kato_proespera}
          splitHour={splitHour}
          hasEstimate={false}
        />
      </div>
    )}
  </PanelCard>
);

export default LevelsPanel;
